import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { shortDay } from '@/lib/format';
import { FactLines, StatusPill } from './parts';

type Timeline = Extract<WireArtifact, { kind: 'timeline' }>;
type Event = Timeline['events'][number];

const KIND_TITLE = { pickup: 'Pickup', delivery: 'Delivery', stop: 'Stop' } as const;

/**
 * The load's stops in the provider's order: the day and the appointment text on
 * the left, a dot and line, then the place and whatever the mapper had for that
 * stop. The appointment text is never re-parsed, so it is shown as it came.
 */
export function TimelineArtifact({ artifact }: { artifact: Timeline }): JSX.Element {
  const last = artifact.events.length - 1;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="font-mono text-[15px] font-medium">
            #{artifact.reference.replace(/^#/, '')}
          </span>
          {artifact.customer !== undefined && (
            <div className="text-mute mt-0.5 truncate text-[12.5px]">{artifact.customer}</div>
          )}
        </div>
        {artifact.status !== undefined && <StatusPill status={artifact.status} />}
      </div>
      <div className="flex flex-col">
        {artifact.events.map((event, i) => (
          <Stop key={i} event={event} line={i < last} />
        ))}
      </div>
    </div>
  );
}

function Stop({ event, line }: { event: Event; line: boolean }): JSX.Element {
  return (
    <div className="grid min-h-16 grid-cols-[70px_22px_minmax(0,1fr)] gap-2.5">
      <div className="pt-0.5 text-right">
        {event.day !== undefined && (
          <div className="font-mono text-[11px] font-medium tabular-nums">
            {shortDay(event.day)}
          </div>
        )}
        <div className="text-mute font-mono text-[11px] leading-[1.4] [overflow-wrap:anywhere]">
          {event.at ?? '—'}
        </div>
      </div>
      <div className="flex flex-col items-center">
        <span
          className="mt-[3px] size-3 flex-none rounded-full border-2"
          style={{
            borderColor: event.kind === 'stop' ? 'var(--mute)' : 'var(--acc)',
            background: event.kind === 'delivery' ? 'var(--acc)' : 'transparent',
          }}
        />
        {line && <span className="bg-line my-1 w-0.5 flex-1" />}
      </div>
      <div className="min-w-0 pb-4">
        <div className="text-[13.5px] font-medium">{KIND_TITLE[event.kind]}</div>
        <div className="text-mute mt-0.5 text-[12.5px] leading-[1.45] [overflow-wrap:anywhere]">
          {event.place}
        </div>
        <FactLines facts={event.facts} />
      </div>
    </div>
  );
}
